import { useState } from 'react';
import { api } from '../api.js';
import { typeName } from '../resourceTypes.js';
import {
  Button, ExportButton, Field, FilterBar, Header, Modal, Pagination, Select, Status, Table,
  can, dateTime, dayLabel, durationLabel, isStaff, money, number, paymentNames, queryString, timeOnly, today, useApp, useAsk, useQuery,
} from '../ui.jsx';

const methodOptions = Object.entries(paymentNames).map(([id, name]) => ({ id, name }));

export function BillsPage() {
  const { auth, stores, storeId, setStoreId, fail, success } = useApp();
  const [filters, setFilters] = useState(() => ({
    store_id: storeId || String(stores[0]?.id || ''),
    from: today(),
    to: today(),
    status: '',
    payment_method: '',
    search: '',
  }));
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [ask, dialog] = useAsk();
  const staff = isStaff(auth);

  useEffect(() => {
    if (storeId && filters.store_id !== storeId) {
      setFilters((current) => ({ ...current, store_id: storeId }));
      setPage(1);
    }
  }, [storeId]);

  const list = useQuery(`/bills${queryString({ ...filters, page })}`);
  const setFilter = (key, value) => { setFilters((current) => ({ ...current, [key]: value })); setPage(1); };
  const show = async (row) => { try { setDetail(await api.get(`/bills/${row.id}`)); } catch (error) { fail(error); } };
  const voidBill = async (row) => {
    const values = await ask({ title: `Hủy hóa đơn ${row.code}?`, danger: true, confirmLabel: 'Hủy hóa đơn', message: `Thực thu ${money(row.total_amount)}. Dòng thu tương ứng trong ca sẽ bị hủy theo; hóa đơn vẫn được lưu để đối chiếu. Chỉ hủy được khi ca còn mở.`, fields: [{ key: 'reason', label: 'Lý do hủy', required: true, minLength: 3 }] });
    if (!values) return;
    try { await api.post(`/bills/${row.id}/void`, { reason: values.reason }); success('Đã hủy hóa đơn'); list.reload(); } catch (error) { fail(error); }
  };
  return <main className="content-page billing-bills-page">
    <Header eyebrow="HÓA ĐƠN" title="Hóa đơn tính tiền" description={staff ? 'Hóa đơn thu tiền mặt trong ngày hôm nay' : 'Mỗi phiên chơi đã thu tiền tạo một hóa đơn và một dòng thu trong ca'} />
    <FilterBar>
      <Select
        label="Cơ sở"
        value={filters.store_id}
        onChange={(value) => {
          setFilter('store_id', value);
          if (value && setStoreId) setStoreId(value);
        }}
        options={stores}
        placeholder="Tất cả cơ sở"
      />
      {!staff && <>
        <Field label="Từ ngày" type="date" value={filters.from} onChange={(value) => setFilter('from', value)} />
        <Field label="Đến ngày" type="date" value={filters.to} onChange={(value) => setFilter('to', value)} />
        <Select label="PTTT" value={filters.payment_method} onChange={(value) => setFilter('payment_method', value)} options={methodOptions} placeholder="Tất cả" />
      </>}
      <Select label="Trạng thái" value={filters.status} onChange={(value) => setFilter('status', value)} options={[{ id: 'paid', name: 'Đã thu' }, { id: 'void', name: 'Đã hủy' }]} placeholder="Tất cả" />
      <Field label="Tìm" value={filters.search} onChange={(value) => setFilter('search', value)} placeholder="Mã hóa đơn, tên, SĐT" />
      <ExportButton path={`/bills${queryString({ ...filters, format: 'csv' })}`} filename="hoa-don.csv" onError={fail} />
    </FilterBar>
    <section className="card table-card">
      <Table rows={list.rows} loading={list.loading} error={list.error} onRetry={list.reload} empty="Không có hóa đơn nào khớp bộ lọc" rowClass={(row) => (row.status === 'void' ? 'row-void' : '')} columns={[
        { key: 'code', label: 'Mã', render: (v, row) => <>{v}{row.source === 'station' && <small> · Station{row.source_code ? ` ${row.source_code}` : ''}</small>}{Boolean(row.needs_review) && <small className="field-error"> · cần xem lại</small>}</> }, { key: 'created_at', label: 'Thời gian', render: dateTime }, { key: 'store_name', label: 'Cơ sở' },
        { key: 'resource_name', label: 'Máy', render: (v, row) => `${v} · ${typeName(row.resource_type)}` }, { key: 'customer_name', label: 'Khách' },
        { key: 'play_minutes', label: 'Giờ chơi', render: durationLabel }, { key: 'play_amount', label: 'Tiền giờ', align: 'right', render: money },
        { key: 'items_amount', label: 'Dịch vụ', align: 'right', render: money }, { key: 'total_amount', label: 'Thực thu', align: 'right', render: (v) => <strong>{money(v)}</strong> },
        { key: 'payment_method', label: 'PTTT', render: (v) => paymentNames[v] }, { key: 'created_by_name', label: 'Thu ngân' }, { key: 'status', label: 'Trạng thái', render: (v) => <Status value={v} /> },
      ]} actions={(row) => <>
        <button className="table-action" onClick={() => show(row)}>Chi tiết</button>
        {row.status === 'paid' && row.source !== 'station' && can(auth, 'cash_shifts.approve') && <button className="table-action delete" onClick={() => voidBill(row)}>Hủy</button>}
      </>} />
      <Pagination pagination={list.pagination} onPage={setPage} />
    </section>
    {detail && <Modal title={`Hóa đơn ${detail.code}`} onClose={() => setDetail(null)}>
      <p className="modal-note">{detail.store_name} · {detail.resource_name} ({typeName(detail.resource_type)}) · {detail.customer_name}{detail.phone ? ` · ${detail.phone}` : ''}<br />{dateTime(detail.started_at)} → {timeOnly(detail.ended_at)} · {durationLabel(detail.play_minutes)} · thu ngân {detail.created_by_name}</p>
      <div className="bill-lines">
        {detail.lines.map((line, index) => <div className="bill-line" key={index}><span>{line.label}{line.note ? ` (${line.note})` : ''}<small>{line.type === 'item' ? `${line.quantity} × ${money(line.unit_price)}` : `${durationLabel(line.minutes)}${line.rate != null ? ` × ${money(line.rate)}/giờ` : ''}`}</small></span><b>{money(line.type === 'item' ? line.amount : (line.price ?? Math.round((line.minutes / 60) * line.rate)))}</b></div>)}
        {Number(detail.play_amount) !== Number(detail.play_amount_calculated) && <div className="bill-line"><span>Tiền giờ đã sửa tay <small>hệ thống tính {money(detail.play_amount_calculated)}</small></span><b>{money(detail.play_amount)}</b></div>}
        {Number(detail.discount_amount) > 0 && <div className="bill-line"><span>Giảm giá</span><b className="neg">−{money(detail.discount_amount)}</b></div>}
        {Number(detail.deposit_deducted) > 0 && <div className="bill-line"><span>Trừ tiền cọc</span><b className="neg">−{money(detail.deposit_deducted)}</b></div>}
        <div className="bill-line total"><span>Thực thu ({paymentNames[detail.payment_method]})</span><b>{money(detail.total_amount)}</b></div>
      </div>
      {!staff && detail.payment_method === 'mixed' && <p className="modal-note">Tiền mặt {money(detail.paid_cash)} · Chuyển khoản {money(detail.paid_transfer)} · Khác {money(detail.paid_other)}</p>}
      {!staff && Number(detail.debt_amount) > 0 && <p className="modal-note">Khách còn nợ: {money(detail.debt_amount)}</p>}
      {detail.source === 'station' && <p className="modal-note">Lập từ OnePlay Station{detail.source_code ? ` (đơn ${detail.source_code})` : ''}. Muốn hủy, hãy hủy đơn ở Station.</p>}
      {Boolean(detail.needs_review) && <p className="field-error">Cần xem lại: {detail.review_note}</p>}
      {detail.notes && <p className="modal-note">Ghi chú: {detail.notes}</p>}
      {detail.status === 'void' && <p className="field-error">Đã hủy bởi {detail.voided_by_name} lúc {dateTime(detail.voided_at)}: {detail.void_reason}</p>}
      <div className="form-actions"><Button onClick={() => setDetail(null)}>Đóng</Button></div>
    </Modal>}
    {dialog}
  </main>;
}

const firstOfMonth = () => `${today().slice(0, 8)}01`;
const numeric = ['bills', 'play_minutes'];

export function RevenuePage() {
  const { stores, storeId: globalStoreId, setStoreId: setGlobalStoreId, fail } = useApp();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [storeId, setStoreId] = useState(() => globalStoreId || String(stores[0]?.id || ''));

  useEffect(() => {
    if (globalStoreId && storeId !== globalStoreId) {
      setStoreId(globalStoreId);
    }
  }, [globalStoreId]);

  const params = { from, to, store_id: storeId };
  const report = useQuery(from && to ? `/reports/revenue${queryString(params)}` : null);
  const rows = report.rows || [];
  const sum = (key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);
  const columns = (report.data?.columns || []).map((column) => ({
    key: column.key, label: column.label, align: ['day', 'store_name'].includes(column.key) ? 'left' : 'right',
    render: column.key === 'day' ? dayLabel : column.key === 'store_name' ? undefined : numeric.includes(column.key) ? (v) => number.format(Number(v)) : money,
  }));
  return <main className="content-page billing-revenue-page">
    <Header eyebrow="BÁO CÁO" title="Doanh thu" description="Tổng hợp hóa đơn còn hiệu lực theo ngày và cơ sở, trong phạm vi cơ sở của bạn" />
    <FilterBar>
      <Field label="Từ ngày" type="date" value={from} onChange={setFrom} required />
      <Field label="Đến ngày" type="date" value={to} onChange={setTo} required />
      <Select
        label="Cơ sở"
        value={storeId}
        onChange={(value) => {
          setStoreId(value);
          if (value && setGlobalStoreId) setGlobalStoreId(value);
        }}
        options={stores}
        placeholder="Toàn phạm vi"
      />
      <ExportButton path={`/reports/revenue${queryString({ ...params, format: 'csv' })}`} filename={`doanh-thu_${from}_${to}.csv`} onError={fail} />
    </FilterBar>
    <section className="ops-summary-grid four">
      <article className="card ops-highlight"><span>Thực thu</span><strong>{money(sum('total_amount'))}</strong><small>{number.format(sum('bills'))} hóa đơn</small></article>
      <article className="card ops-highlight"><span>Tiền mặt</span><strong>{money(sum('cash_amount'))}</strong><small>vào quỹ ca</small></article>
      <article className="card ops-highlight"><span>Chuyển khoản / khác</span><strong>{money(sum('transfer_amount') + sum('other_amount'))}</strong><small>không vào quỹ tiền mặt</small></article>
      <article className="card ops-highlight"><span>Tiền giờ / dịch vụ</span><strong>{money(sum('play_amount'))}</strong><small>dịch vụ {money(sum('items_amount'))}</small></article>
    </section>
    <section className="card table-card">
      <Table rows={report.rows} columns={columns} loading={report.loading} error={report.error} onRetry={report.reload} rowKey={(row) => `${row.day}-${row.store_id}`} empty="Chưa có hóa đơn trong khoảng thời gian này" />
    </section>
  </main>;
}
